import { useCallback, useState } from 'react';

import { IPreviewRecipientOption } from './preview-panel.types';

interface IUsePreviewRecipientReturn {
  // null = the server-picked default (first) recipient; set = an explicit pick.
  selected: IPreviewRecipientOption | null;
  fellOut: boolean;
  selectRecipient: (option: IPreviewRecipientOption | null) => void;
  resetToDefault: () => void;
  clearToDefault: () => void;
  dismissFellOut: () => void;
}

// HOOK: owns the compose preview's "preview as" recipient selection — the picked
// exemplar (null = default) and the fell-out notice flag.
export const usePreviewRecipient = (): IUsePreviewRecipientReturn => {
  const [selected, setSelected] = useState<IPreviewRecipientOption | null>(null);
  const [fellOut, setFellOut] = useState(false);

  const selectRecipient = useCallback((option: IPreviewRecipientOption | null) => {
    setSelected(option);
    setFellOut(false);
  }, []);

  // A criteria change dropped the picked recipient — fall back to the default and
  // flag it so the panel surfaces a brief notice.
  const resetToDefault = useCallback(() => {
    setSelected(null);
    setFellOut(true);
  }, []);

  // Audience emptied under the pick — drop it without the fell-out notice; the
  // empty-audience placeholder already explains why nothing renders.
  const clearToDefault = useCallback(() => {
    setSelected(null);
    setFellOut(false);
  }, []);

  const dismissFellOut = useCallback(() => setFellOut(false), []);

  return { selected, fellOut, selectRecipient, resetToDefault, clearToDefault, dismissFellOut };
};
